/**
 * PasskeyManager — V1.9.8
 *
 * A self-contained card that lists the current user's enrolled passkeys,
 * lets them add a new one (calls `registerPasskey`), and remove existing
 * ones. Designed to be dropped into any settings page (recruiter or
 * candidate) without further wiring.
 */

import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, Trash, ShieldCheck, Plus } from "@phosphor-icons/react";
import {
    hasPlatformAuthenticator,
    listPasskeys,
    registerPasskey,
    deletePasskey,
    passkeysSupported,
} from "@/lib/passkey";

export default function PasskeyManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [enrolling, setEnrolling] = useState(false);
    const [platformOk, setPlatformOk] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listPasskeys();
            setItems(res.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Failed to load passkeys.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        hasPlatformAuthenticator().then(setPlatformOk);
    }, [refresh]);

    const enroll = async () => {
        setEnrolling(true);
        try {
            const res = await registerPasskey();
            toast.success(`Passkey added (${res.label || "device"}).`);
            await refresh();
        } catch (e) {
            toast.error(e.message || "Could not enroll passkey.");
        } finally {
            setEnrolling(false);
        }
    };

    const remove = async (credId, label) => {
        if (!window.confirm(`Remove passkey "${label}"? You'll have to re-enroll next time.`))
            return;
        try {
            await deletePasskey(credId);
            toast.success("Passkey removed.");
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Could not remove passkey.");
        }
    };

    if (!passkeysSupported()) {
        return (
            <div
                className="bg-white border border-ink/10 rounded-md p-5"
                data-testid="passkey-manager-unsupported"
            >
                <div className="flex items-center gap-2 mb-1">
                    <Fingerprint size={18} weight="bold" className="text-ink/40" />
                    <h3 className="font-heading font-black">Passkeys</h3>
                </div>
                <p className="text-sm text-ink/60">
                    Passkeys need a modern browser served over HTTPS. Please switch to Chrome,
                    Safari or Edge on a passkey-capable device to enroll one.
                </p>
            </div>
        );
    }

    return (
        <div
            className="bg-white border border-ink/10 rounded-md p-5"
            data-testid="passkey-manager"
        >
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <ShieldCheck size={18} weight="bold" className="text-cobalt" />
                        <h3 className="font-heading font-black">Passkeys</h3>
                    </div>
                    <p className="text-xs text-ink/60 mt-1 max-w-md">
                        Sign in with Face&nbsp;ID, Touch&nbsp;ID, Windows Hello, or your
                        phone's fingerprint. Faster than a password, and phishing-resistant.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={enroll}
                    disabled={enrolling || !platformOk}
                    data-testid="passkey-enroll-btn"
                    className="btn-gradient inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-black uppercase tracking-wider disabled:opacity-40 rounded-md shrink-0"
                    title={platformOk ? "" : "No platform authenticator on this device"}
                >
                    <Plus size={12} weight="bold" />
                    {enrolling ? "Waiting for biometric…" : "Add passkey"}
                </button>
            </div>

            {loading ? (
                <p className="text-xs text-ink/40 py-6 text-center">Loading passkeys…</p>
            ) : items.length === 0 ? (
                <div
                    data-testid="passkey-empty-state"
                    className="border border-dashed border-ink/15 rounded-md p-5 text-center bg-cream/30"
                >
                    <Fingerprint size={28} weight="light" className="mx-auto mb-2 text-ink/30" />
                    <p className="text-sm text-ink/60">
                        No passkeys enrolled yet. Add one to skip typing your password.
                    </p>
                </div>
            ) : (
                <ul className="divide-y divide-ink/10" data-testid="passkey-list">
                    {items.map((p) => (
                        <li
                            key={p.credential_id}
                            className="flex items-center justify-between gap-3 py-3"
                            data-testid={`passkey-item-${p.credential_id.slice(0, 8)}`}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-full bg-cobalt/10 text-cobalt inline-flex items-center justify-center shrink-0">
                                    <Fingerprint size={16} weight="bold" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-bold truncate">
                                        {p.label || "Passkey"}
                                        {p.backed_up && (
                                            <span className="ml-2 text-[9px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                                                synced
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-[11px] text-ink/50">
                                        Added {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => remove(p.credential_id, p.label || "Passkey")}
                                data-testid={`passkey-remove-${p.credential_id.slice(0, 8)}`}
                                aria-label="Remove passkey"
                                className="p-2 text-ink/40 hover:text-red-600 hover:bg-red-50 rounded-md transition"
                            >
                                <Trash size={14} weight="bold" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
